import React, { Suspense, useEffect } from 'react';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import {
  Colors,
  Flex,
  IconChevronDown,
  IconChevronUp,
  List,
  RemoteImage,
  TextButton,
  Typography,
} from '@banhala/ui-kit';
import type { GoodsReview } from 'shared/src/api/goodsService/getGoodsReviews';
import { useNavigator } from 'shared/src/utils';
import { useReviewCodiGoods } from '../../../queries/useReviewCodiGoods';

type ReviewCodiGoodsProps = {
  review: GoodsReview;
};

export const ReviewCodiGoods: React.FC<ReviewCodiGoodsProps> = ({ review }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  return (
    <Flex>
      <Flex.Row
        alignItems={'center'}
        justifyContent={'flex-end'}
        margin={{ right: -16 }}
      >
        <TextButton
          size={'small'}
          color={'black'}
          onPress={() => setIsExpanded(prev => !prev)}
          icon={isExpanded ? <IconChevronUp /> : <IconChevronDown />}
        >
          코디 정보 보기
        </TextButton>
      </Flex.Row>
      <Suspense fallback={<CodiGoodsListSkeleton />}>
        <CodiGoodsListContent
          writer={review.writer}
          reviewSno={review.sno}
          expanded={isExpanded}
        />
      </Suspense>
    </Flex>
  );
};

const CodiGoodsListContent: React.FC<{
  reviewSno: number;
  expanded: boolean;
  writer: string;
}> = ({ reviewSno, expanded, writer }) => {
  const { data } = useReviewCodiGoods(reviewSno);
  const heightAnim = useSharedValue<number>(0);

  const { navigator } = useNavigator();
  const animatedStyle = useAnimatedStyle(() => ({
    height: heightAnim.value,
    overflow: 'hidden',
    marginHorizontal: -16,
    backgroundColor: Colors.bg_secondary,
  }));

  useEffect(() => {
    heightAnim.value = withTiming(expanded ? 132 : 0, {
      duration: 300,
      easing: Easing.inOut(Easing.quad),
    });
  }, [expanded, heightAnim]);

  const handlePressGoods = (goodsSno: number) => {
    navigator.moveToGoods({
      sno: goodsSno,
      firstPageRendering: null,
    });
    // bridge.openDeeplink(`ably://goods/${goodsSno}`, {
    //   webCallback: () => {},
    // });
  };

  return (
    <Animated.View style={animatedStyle}>
      {/*<Headline headline={`???`} size={'medium'} />*/}
      <Flex padding={{ horizontal: 16, top: 20, bottom: 16 }}>
        <Typography type={'subtitle2'}>
          {writer}님이 같이 코디한 상품
        </Typography>
      </Flex>
      <List
        style={{ marginTop: 4 }}
        horizontal
        contentContainerStyle={{ rowGap: 12, paddingHorizontal: 16 }}
        data={data.codi_goods}
        showsHorizontalScrollIndicator={false}
        renderItem={({ item: goods }) => {
          return (
            <Flex.Row
              key={goods.sno}
              columnGap={12}
              width={220}
              height={56}
              margin={{ right: 8 }}
              onPress={() => handlePressGoods(goods.sno)}
            >
              <RemoteImage
                uri={goods.image}
                width={56}
                height={56}
                borderRadius={'small'}
              />
              <Flex flex={1} justifyContent={'center'} rowGap={4}>
                <Typography
                  type={'body3'}
                  color={'content_primary'}
                  numberOfLines={1}
                >
                  {goods.name}
                </Typography>
                <Flex.Row alignItems={'center'} columnGap={4}>
                  {goods.discount_rate > 0 && (
                    <Typography
                      type={'subtitle3'}
                      color={'content_pink_primary'}
                    >
                      {goods.discount_rate}%
                    </Typography>
                  )}
                  <Typography type={'subtitle3'} color={'content_primary'}>
                    {goods.price.toLocaleString()}원
                  </Typography>
                </Flex.Row>
              </Flex>
            </Flex.Row>
          );
        }}
      />
    </Animated.View>
  );
};

const CodiGoodsListSkeleton: React.FC = () => {
  return (
    <Flex margin={{ top: 12 }} rowGap={12}>
      {[1, 2].map(i => (
        <Flex.Row key={i} columnGap={12}>
          <Flex
            width={64}
            height={64}
            borderRadius={4}
            backgroundColor={'bg_secondary'}
          />
          <Flex flex={1} justifyContent={'center'} rowGap={4}>
            <Flex
              width={60}
              height={14}
              borderRadius={2}
              backgroundColor={'bg_secondary'}
            />
            <Flex
              width={120}
              height={16}
              borderRadius={2}
              backgroundColor={'bg_secondary'}
            />
            <Flex
              width={80}
              height={16}
              borderRadius={2}
              backgroundColor={'bg_secondary'}
            />
          </Flex>
        </Flex.Row>
      ))}
    </Flex>
  );
};
